// The line between the chat and a docked canvas: drag it, or focus it and use the arrow keys, to give either side
// more room; a double-click returns to the default split. It sets one value on the shell, the canvas's share of the
// room beside the sidebar (`--ws-canvas-track`, workspace.css), and the grid keeps both minimum widths
// (`--ws-chat-min`, `--ws-canvas-min`, registered lengths read here in px) whatever the share asks. Shown from
// 1101px, where that grid places the two side by side; narrower, the canvas covers them, or (beside the Library from
// 901px) keeps the Library's own fixed split. The chosen share is this device's preference for every canvas
// (presentation only). As a window splitter it names and reports the canvas, the pane it resizes beside whatever
// page or chat sits left of it, between the two minimums.
import { useLayoutEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { useNamespaceCopy } from '@/i18n/ns';
import canvasNavCopy from '@/i18n/ns/canvas-nav';

const STORAGE_KEY = 'poneva.workspace.canvasShare';
const STEP = 0.02;
// A press that moves less than this is a click, not a drag.
const DRAG_START_PX = 3;
/** The canvas the separator resizes (canvas.tsx). */
export const CANVAS_ID = 'ws-canvas';

function storedShare() {
  try {
    const value = Number(localStorage.getItem(STORAGE_KEY));
    return value > 0 && value < 1 ? value : null;
  } catch {
    return null;
  }
}
function keepShare(share: number | null) {
  try {
    if (share === null) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, String(share));
  } catch {
    // Blocked storage (a private window) keeps nothing: each canvas opens at the default split.
  }
}

/** The shell, its chat column and the canvas around one divider. */
function frame(divider: HTMLElement | null) {
  const shell = divider?.closest<HTMLElement>('.ws-shell');
  const chat = shell?.querySelector<HTMLElement>(':scope > .ws-main-column');
  const canvas = divider?.parentElement;
  if (!shell || !chat || !canvas) return null;
  const room = shell.getBoundingClientRect().right - chat.getBoundingClientRect().left;
  return { shell, chat, canvas, room };
}

function applyShare(shell: HTMLElement, share: number | null) {
  if (share === null) shell.style.removeProperty('--ws-canvas-track');
  else
    shell.style.setProperty(
      '--ws-canvas-track',
      `calc((100% - var(--ws-nav-width)) * ${share.toFixed(4)})`,
    );
}

type Reading = { now: number; min: number; max: number };

export function CanvasDivider() {
  const copy = useNamespaceCopy(canvasNavCopy);
  const divider = useRef<HTMLDivElement>(null);
  // While pressed: where it went down, where on the line it took hold (so the line does not jump to the pointer),
  // and whether it has become a drag (a plain click keeps the split as it was).
  const drag = useRef<{ startX: number; offset: number; moved: boolean } | null>(null);
  // The canvas's share of the room as the grid drew it, and the shares the two minimums allow.
  const [reading, setReading] = useState<Reading | null>(null);
  const measure = () => {
    const parts = frame(divider.current);
    if (!parts || parts.room <= 0) return null;
    const share = parts.canvas.getBoundingClientRect().width / parts.room;
    const style = getComputedStyle(parts.shell);
    const chatMin = parseFloat(style.getPropertyValue('--ws-chat-min')) || 0;
    const canvasMin = parseFloat(style.getPropertyValue('--ws-canvas-min')) || 0;
    const clamp = (value: number) => Math.min(Math.max(value, 0), 1);
    setReading({
      now: share,
      min: clamp(canvasMin / parts.room),
      max: clamp((parts.room - chatMin) / parts.room),
    });
    return share;
  };
  useLayoutEffect(() => {
    const parts = frame(divider.current);
    if (!parts) return;
    applyShare(parts.shell, storedShare());
    measure();
    // A window resize or the compact sidebar changes the chat's share; the separator reports it.
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(parts.canvas);
    // While the canvas is held at its minimum, only the chat column's width shows the room changing.
    observer?.observe(parts.chat);
    return () => {
      observer?.disconnect();
      applyShare(parts.shell, null);
      // A canvas replaced or closed mid-drag must not leave the shell unselectable.
      parts.shell.removeAttribute('data-resizing');
    };
  }, []);
  // Ask for a share, then keep the one the grid could actually give (null: the default split).
  const settle = (share: number | null) => {
    const parts = frame(divider.current);
    if (!parts) return;
    applyShare(parts.shell, share);
    const drawn = measure();
    if (share === null || drawn === null) return keepShare(null);
    applyShare(parts.shell, drawn);
    keepShare(drawn);
  };
  const follow = (event: PointerEvent<HTMLDivElement>) => {
    const parts = frame(divider.current);
    if (!parts || parts.room <= 0) return;
    const edge = event.clientX - (drag.current?.offset ?? 0);
    const canvasWidth = parts.shell.getBoundingClientRect().right - edge;
    // Only written while dragging; what the grid drew is read once, at the end.
    applyShare(parts.shell, Math.min(Math.max(canvasWidth / parts.room, 0), 1));
  };
  const end = (event: PointerEvent<HTMLDivElement>) => {
    const was = drag.current;
    if (!was) return;
    drag.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    frame(divider.current)?.shell.removeAttribute('data-resizing');
    if (was.moved) settle(measure());
  };
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const parts = frame(divider.current);
    if (!parts || parts.room <= 0) return;
    const share = parts.canvas.getBoundingClientRect().width / parts.room;
    // The line moves the way the arrow points; Home gives the canvas its least room, End its most.
    const next = {
      ArrowLeft: share + STEP,
      ArrowRight: share - STEP,
      Home: 0,
      End: 1,
    }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    settle(Math.min(Math.max(next, 0), 1));
  };
  const percent = (value: number) => Math.round(value * 100);
  return (
    <div
      ref={divider}
      className="ws-canvas-divider"
      role="separator"
      aria-orientation="vertical"
      aria-label={copy.canvasWidth}
      aria-controls={CANVAS_ID}
      aria-valuemin={reading ? percent(reading.min) : 0}
      aria-valuemax={reading ? percent(reading.max) : 100}
      aria-valuenow={reading ? percent(reading.now) : undefined}
      tabIndex={0}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        const canvas = frame(divider.current)?.canvas.getBoundingClientRect();
        drag.current = {
          startX: event.clientX,
          offset: canvas ? event.clientX - canvas.left : 0,
          moved: false,
        };
        event.currentTarget.setPointerCapture?.(event.pointerId);
        frame(divider.current)?.shell.setAttribute('data-resizing', '');
      }}
      onPointerMove={(event) => {
        const pressed = drag.current;
        if (!pressed) return;
        if (!pressed.moved && Math.abs(event.clientX - pressed.startX) < DRAG_START_PX) return;
        pressed.moved = true;
        follow(event);
      }}
      onPointerUp={end}
      onPointerCancel={end}
      onLostPointerCapture={end}
      onDoubleClick={() => settle(null)}
      onKeyDown={onKeyDown}
    />
  );
}
